import { Capacitor } from "@capacitor/core";

/**
 * Detecta se estamos rodando dentro de um container nativo Capacitor
 * (iOS/Android). Retorna `false` no navegador / PWA web.
 */
export const isNativePlatform = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Tipo mínimo do plugin @capacitor/geolocation que usamos. Mantemos local
 * para evitar importar tipos do pacote no bundle web.
 */
export type NativeGeolocationPlugin = {
  checkPermissions: () => Promise<{ location: string }>;
  requestPermissions: () => Promise<{ location: string }>;
  getCurrentPosition: (opts: {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
  }) => Promise<{ coords: { latitude: number; longitude: number; accuracy: number } }>;
  watchPosition: (
    opts: { enableHighAccuracy?: boolean; timeout?: number; maximumAge?: number },
    cb: (
      pos: { coords: { latitude: number; longitude: number; accuracy: number } } | null,
      err?: unknown,
    ) => void,
  ) => Promise<string>;
  clearWatch: (opts: { id: string }) => Promise<void>;
};

/**
 * Carrega o plugin @capacitor/geolocation **somente** quando rodando em
 * ambiente nativo. No navegador/PWA, retorna `null` sem nunca executar o
 * `import()` — assim evitamos chamadas que dependem do bridge nativo e
 * mantemos o caminho web previsível usando `navigator.geolocation`.
 *
 * O `try/catch` cobre cenários edge (plugin não instalado em build nativo
 * legado, falha de carregamento do chunk, etc.) sem derrubar a aplicação.
 */
export async function loadNativeGeolocation(): Promise<NativeGeolocationPlugin | null> {
  if (!isNativePlatform()) return null;
  try {
    const mod = await import("@capacitor/geolocation");
    return mod.Geolocation as unknown as NativeGeolocationPlugin;
  } catch (err) {
    console.warn("[geolocation] failed to load native plugin, falling back to web", err);
    return null;
  }
}
